import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore';

// Mirror authStore's hardcoded production API base (the whole SPA talks to
// the prod API regardless of where it's served from).
const API_URL = 'https://api.hitzlink.com/api';

export type AccountRole = 'owner' | 'editor' | 'viewer';
export type AccountPlan = 'free' | 'pro' | 'agency' | 'enterprise';

export interface WorkspaceAccount {
  id: string;
  name: string;
  email: string | null;
  role: AccountRole;
  isSelf: boolean;
  /** The workspace OWNER's plan tier — used for plan-gated UI. */
  plan: AccountPlan;
}

interface AccountState {
  /** All workspaces the user can act in (their own + team memberships). */
  accounts: WorkspaceAccount[];
  /** The workspace currently being acted in. null = own account. */
  activeAccountId: string | null;
  loaded: boolean;
  loadAccounts: () => Promise<void>;
  setActiveAccount: (id: string) => void;
  /** Role of the user within the active workspace. */
  getActiveRole: () => AccountRole;
  /** The active workspace object (or null while loading). */
  getActiveAccount: () => WorkspaceAccount | null;
  /** Plan tier of the active workspace owner (or null while loading). */
  getActivePlan: () => AccountPlan | null;
  reset: () => void;
}

function normalizePlan(raw: unknown): AccountPlan {
  if (raw === 'plus') return 'pro';
  if (raw === 'pro' || raw === 'agency' || raw === 'enterprise' || raw === 'free') {
    return raw;
  }
  return 'free';
}

export const useAccountStore = create<AccountState>()(
  persist(
    (set, get) => ({
      accounts: [],
      activeAccountId: null,
      loaded: false,

      loadAccounts: async () => {
        const token = useAuthStore.getState().token;
        if (!token) return;
        try {
          const res = await fetch(`${API_URL}/team/accounts`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return;
          const data = await res.json();
          const accounts: WorkspaceAccount[] = Array.isArray(data?.accounts)
            ? data.accounts.map((a: WorkspaceAccount) => ({
                ...a,
                plan: normalizePlan(a.plan),
              }))
            : [];

          // Validate the persisted activeAccountId is still accessible; if
          // the membership was revoked (or never set), fall back to self.
          const selfId = useAuthStore.getState().user?.id || null;
          const current = get().activeAccountId;
          const stillValid = current && accounts.some((a) => a.id === current);
          set({
            accounts,
            loaded: true,
            activeAccountId: stillValid ? current : selfId,
          });
        } catch (err) {
          console.warn('[accountStore] loadAccounts failed:', err);
        }
      },

      setActiveAccount: (id: string) => {
        set({ activeAccountId: id });
      },

      getActiveRole: () => {
        const { accounts, activeAccountId } = get();
        const selfId = useAuthStore.getState().user?.id || null;
        const id = activeAccountId || selfId;
        const acc = accounts.find((a) => a.id === id);
        return acc?.role || 'owner';
      },

      getActiveAccount: () => {
        const { accounts, activeAccountId } = get();
        const selfId = useAuthStore.getState().user?.id || null;
        const id = activeAccountId || selfId;
        return accounts.find((a) => a.id === id) || null;
      },

      getActivePlan: () => {
        const acc = get().getActiveAccount();
        return acc?.plan ?? null;
      },

      reset: () => set({ accounts: [], activeAccountId: null, loaded: false }),
    }),
    {
      name: 'account-context',
      // Only persist the chosen workspace; the accounts list is always
      // re-fetched on load so it can't go stale across sessions.
      partialize: (state) => ({ activeAccountId: state.activeAccountId }),
    }
  )
);

/**
 * Non-hook accessor for the header to send on API requests. Returns the
 * active account id ONLY when the user has switched into another workspace
 * (different from their own id). On their own account we send nothing so
 * the server defaults to self — keeping owner traffic byte-identical to
 * the pre-team behaviour.
 */
export function getActiveAccountHeader(): string | null {
  const { activeAccountId } = useAccountStore.getState();
  const selfId = useAuthStore.getState().user?.id || null;
  if (!activeAccountId || !selfId) return null;
  if (activeAccountId === selfId) return null;
  return activeAccountId;
}

/**
 * Plan tier that should drive feature gates / upgrade modals.
 * Prefer the active workspace OWNER's plan; fall back to the logged-in
 * user's personal subscription. Use this everywhere instead of reading
 * `user.subscription` directly for product access checks.
 */
export function getEffectivePlan(): AccountPlan {
  const active = useAccountStore.getState().getActivePlan();
  if (active) return active;
  const personal = useAuthStore.getState().user?.subscription;
  return normalizePlan(personal);
}

/** React hook mirror of getEffectivePlan() for components. */
export function useEffectivePlan(): AccountPlan {
  const activePlan = useAccountStore((s) => s.getActivePlan());
  const personal = useAuthStore((s) => s.user?.subscription);
  return activePlan ?? normalizePlan(personal);
}
